/**
 * AppRestarter Plugin - Lazy Loading Version
 *
 * Uses Capacitor.isPluginAvailable() to check if the native plugin exists
 * before registering, preventing "not implemented on android" errors.
 *
 * Methods:
 * - restartApp({ reason, delayMs }): Hard restart - kills process and relaunches
 * - reloadWebView({ reason }): Soft restart - only reloads the WebView
 * - getRestartInfo(): Get info about the last restart (if any)
 */

let _pluginInstance = null;
let _initializationPromise = null;

/**
 * Web fallback implementation
 */
class AppRestarterWeb {
  async restartApp(options = {}) {
    console.log('[AppRestarterWeb] Falling back to page reload');
    window.location.reload();
    return { success: true, message: 'Page reloaded (web fallback)' };
  }

  async reloadWebView(options = {}) {
    console.log('[AppRestarterWeb] Falling back to page reload');
    window.location.reload();
    return { success: true, message: 'Page reloaded (web fallback)' };
  }

  async getRestartInfo() {
    return { wasRestarted: false };
  }
}

/**
 * Initialize the plugin (lazy, thread-safe)
 */
const getPlugin = async () => {
  if (_pluginInstance) {
    return _pluginInstance;
  }

  if (_initializationPromise) {
    return _initializationPromise;
  }

  _initializationPromise = (async () => {
    try {
      const { Capacitor, registerPlugin } = await import('@capacitor/core');

      if (Capacitor.isPluginAvailable('AppRestarter')) {
        _pluginInstance = registerPlugin('AppRestarter');
        console.log('[AppRestarter] Native plugin available');
      } else {
        console.log('[AppRestarter] Native plugin not available, using web fallback');
        _pluginInstance = new AppRestarterWeb();
      }
    } catch (err) {
      console.warn('[AppRestarter] Init failed, using web fallback:', err.message);
      _pluginInstance = new AppRestarterWeb();
    }

    return _pluginInstance;
  })();

  return _initializationPromise;
};

/**
 * Proxy object that lazy-loads the actual plugin on first use
 */
const AppRestarter = {
  async restartApp(options = {}) {
    try {
      const plugin = await getPlugin();
      return await plugin.restartApp(options);
    } catch (err) {
      console.error('[AppRestarter] restartApp failed:', err);
      window.location.reload();
      return { success: false, error: err.message };
    }
  },

  async reloadWebView(options = {}) {
    try {
      const plugin = await getPlugin();
      return await plugin.reloadWebView(options);
    } catch (err) {
      console.error('[AppRestarter] reloadWebView failed:', err);
      window.location.reload();
      return { success: false, error: err.message };
    }
  },

  async getRestartInfo() {
    try {
      const plugin = await getPlugin();
      return await plugin.getRestartInfo();
    } catch (err) {
      console.error('[AppRestarter] getRestartInfo failed:', err);
      return { wasRestarted: false, error: err.message };
    }
  },
};

export default AppRestarter;
